import React, {
	memo,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { CollapsibleBlock, DynamicIconWrapper, Icons } from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection';
import { TTab } from '@components/common/ProfileTabSection/ProfileTabSection';
import EmptyStubKpi from '@components/Map/SitesInfo/EmptyStubKpi';

import { useQueryUser } from '@hooks/useQueryUser';
import { useSiteCells } from '@hooks/useSiteCells';

import { auth, kpi } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSelectedVendorOnSitesCard } from '@slices/map';

import { isEmpty, sortObjectByKey } from '@utils/common';
import { toggleCollapsibleBlockMapInfo } from '@utils/toggleCollapsibleBlockMapInfo';

import { TCellsData } from '@interfaces/cells/cells';
import { ISiteProfile } from '@interfaces/cells/siteProfile';
import { ICoords, TVendor } from '@interfaces/map/map';

import { ETechnologiesValues, EVendorsValues, TTechnologiesValues } from '@enums/common';

import { DEFAULT_CLUSTER_ZOOM_LVL } from '@constants/common';
import { routes } from '@constants/routes';
import { collapsibleBlocksInitialState } from '@components/Map/SitesInfo/constants';

import SiteParameters from './SiteParameters';

import styles from './SelectedSiteInfo.module.scss';

interface ISingleSiteInfoProps {
	parametersOnMapComponent: ReactNode;
	site: ISiteProfile;
	panTo: (coords: ICoords, zoomLevel: number) => void;
}

const { adminPanel } = routes;

const SingleSiteInfo = ({
	panTo,
	parametersOnMapComponent,
	site,
}: ISingleSiteInfoProps) => {
	const { accessPermission } = useAppSelector(auth);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const { lastKpiSourceData } = useAppSelector(kpi);

	const {
		networkOperatorKpiUpdater,
		networkOperatorKpiViewer,
		networkOperatorTelecomSitesAttributeUpdater,
		networkOperatorTelecomSitesViewer,
		assetConsumerOrganizationAdministrator,
	} = accessPermission;

	const { user } = useQueryUser();

	const isKpiView = (
		networkOperatorKpiUpdater
		|| networkOperatorKpiViewer
		|| assetConsumerOrganizationAdministrator
	);
	const isSiteProfileAvailable = user?.organization?.id === site.ownerId && (
		networkOperatorTelecomSitesAttributeUpdater || networkOperatorTelecomSitesViewer
	);

	const { vendor } = site;

	const isNokia = vendor.name === EVendorsValues.nokia;

	useEffect(() => {
		if (!vendor) return;

		dispatch(setSelectedVendorOnSitesCard(vendor as TVendor));
	}, [dispatch, vendor]);

	const [
		collapsibleBlocks,
		setCollapsibleBlocks,
	] = useState<Record<string, boolean>>(() => ({
		...collapsibleBlocksInitialState,
		kpiAnalysis: isKpiView && !isEmpty(lastKpiSourceData?.kpiIds),
	}));

	const tabs = useMemo(() => {
		return sortObjectByKey(site.technologies.map((technology) => ({
			title: technology.title,
			value: technology.name,
			isVisible: true,
		})), 'title') as TTab[];
	}, [site.technologies]);

	const [currentTab, setCurrentTab] = useState<TTechnologiesValues>(site.technologies[0]?.name);
	const [cellsData, setCellsData] = useState<TCellsData>();

	const { limenetCells, nokiaCells } = useSiteCells({
		technology: currentTab,
		sites: [site],
	});

	const toggleCollapsibleBlock = useCallback((name: string) => toggleCollapsibleBlockMapInfo({
		name,
		collapsibleBlocks,
		setCollapsibleBlocks,
		initialState: collapsibleBlocksInitialState,
	}), [collapsibleBlocks]);

	const getCurrentTab = useCallback(() => {
		if (!currentTab || isEmpty(cellsData)) return null;

		return ETechnologiesValues[currentTab] && (
			<SiteParameters
				cellsData={cellsData}
				site={site}
				technologyValue={ETechnologiesValues[currentTab]}
			/>
		);
	}, [cellsData, currentTab, site]);

	useEffect(() => {
		setCellsData(isNokia ? nokiaCells[0] : limenetCells[0]);
	}, [isNokia, limenetCells, nokiaCells]);

	useEffect(() => {
		setCurrentTab(site.technologies[0]?.name);
	}, [site.technologies]);

	const redirectToSiteProfile = useCallback(() => {
		return navigate(`${adminPanel.index}/manage-site/site-profile/${site.id}`);
	}, [navigate, site.id]);

	const handlePanToView = useCallback(() => {
		const { lat, lng } = site;

		panTo({ lat, lng }, DEFAULT_CLUSTER_ZOOM_LVL);
	}, [panTo, site]);

	return (
		<div className={styles.siteInfoContainer}>
			<div className={styles.type}>
				Selected
			</div>
			<div className={styles.generalInfoContainer}>
				<div className={styles.siteIdContainer}>
					<span className={styles.fieldName}>Site ID: </span>
					{site.siteId}
					{isSiteProfileAvailable && (
						<DynamicIconWrapper
							icon={Icons.viewIcon}
							onClick={redirectToSiteProfile}
							tooltipLabel='View in Admin Panel'
						/>
					)}
					<DynamicIconWrapper
						icon={Icons.verifyCoordinates}
						onClick={handlePanToView}
						tooltipLabel='Locate site on a map'
						withHover
					/>
				</div>
				<div><span className={styles.fieldName}>Vendor: </span>{site.vendor.title}</div>
				<div><span className={styles.fieldName}>Region: </span>N/A</div>
				<div><span className={styles.fieldName}>City: </span>N/A</div>
				<div><span className={styles.fieldName}>Latitude: </span>{site.lat}</div>
				<div><span className={styles.fieldName}>Longitude: </span>{site.lng}</div>
			</div>
			<CollapsibleBlock
				isClickableRow
				isOpen={collapsibleBlocks.siteParameters}
				label='Site Parameters'
				name='siteParameters'
				toggleState={toggleCollapsibleBlock}
				withSeparatorLine
			>
				<div className={styles.technologyTabs}>
					<ProfileTabSection
						currentTab={currentTab}
						getCurrentTab={getCurrentTab as any}
						isFullWidthTabs
						isWithoutShadow
						setCurrentTab={setCurrentTab}
						tabs={tabs}
					/>
				</div>
			</CollapsibleBlock>
			{isKpiView &&
				<CollapsibleBlock
					isClickableRow
					isOpen={collapsibleBlocks.kpiAnalysis}
					label={<div className={styles.collapsibleLabel}>KPI Analysis</div>}
					name='kpiAnalysis'
					toggleState={toggleCollapsibleBlock}
					withSeparatorLine
				>
					{parametersOnMapComponent || <EmptyStubKpi />}
				</CollapsibleBlock>
			}
		</div>
	);
};

export default memo(SingleSiteInfo);
